import { forwardRef, useRef, useState } from 'react';

import Button from '@/components/common/buttons/Button';
import ResetBtn from '@/components/common/buttons/ResetBtn';
import * as SC from '@/components/modal/Modal.style';
import type { ModalSizeVar } from '@/consts/ModalSizeVar';
import { MODAL_SIZE_VAR } from '@/consts/ModalSizeVar';
import useDebounceClick from '@/hooks/useDebounceClick';
import useKeyboardEvent from '@/hooks/useKeyboardEvent';
import useModalHook from '@/hooks/useModalHook';
import useMount from '@/hooks/useMount';
import usePreventScroll from '@/hooks/usePreventScroll';
import strings from '@/i18n/index';
import useThemeStore from '@/stores/themeStore';

export interface ModalComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  depth?: number;
  hideTitle?: boolean;
  hideFooter?: boolean;
  disabled?: boolean;
  children?: React.ReactElement | string;
  subChildren?: React.ReactNode;
  title: string;
  width?: ModalSizeVar | string;
  height?: string;
  borderRadius?: string;
  boxWrapperStyle?: React.CSSProperties;
  boxStyle?: React.CSSProperties;
  /**
   * dim영역이 닫히지 않게 조절할 수 있습니다.
   */
  isHideAreaFixed?: boolean;
  /**
   * Dim 영역 클릭 가능함 여부
   */
  isDimClickForbidden?: boolean;
  /**
   * Confirm 버튼 Error Theme 여부
   */
  isConfirmError?: boolean;
  cancelText?: string;
  confirmText?: string;
  onSubAction?: () => void;
  onCancel: () => void;
  onConfirm?: () => void;
  onReset?: () => void;
  resetLabel?: string;
  hasBorderBottom?: boolean;
  hideCancelBtn?: boolean;
}

const Modal = forwardRef<HTMLDivElement, ModalComponentProps>(
  (
    {
      className = 'modal-container',
      depth = 1,
      cancelText = strings.SENDBIRD.BUTTON__CANCEL,
      confirmText = strings.CONFIRM,
      hideFooter = false,
      hideTitle = false,
      disabled = false,
      children,
      subChildren,
      title,
      width = MODAL_SIZE_VAR.XXS,
      height,
      borderRadius,
      boxWrapperStyle,
      boxStyle,
      isHideAreaFixed,
      isDimClickForbidden = false,
      isConfirmError = false,
      onCancel,
      onConfirm,
      onSubAction,
      onReset,
      resetLabel,
      hasBorderBottom,
      hideCancelBtn = false,
      ...rest
    },
    ref,
  ) => {
    const { theme } = useThemeStore();
    const { closeAllModal } = useModalHook();
    const [isClicked, setIsClicked] = useState(false);
    const [isConfirmClicked, setIsConfirmClicked] = useDebounceClick();

    const confirmRef = useRef<HTMLButtonElement>(null);

    // escape 입력 시 전체 모달 창 닫기
    useKeyboardEvent('Escape', () => {
      closeAllModal();
    });

    useKeyboardEvent('Enter', () => {
      confirmRef?.current?.click();
    });

    usePreventScroll(depth !== 1);

    const isMount = useMount();
    if (!isMount) {
      return null;
    }

    const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
      if (isClicked) {
        return;
      }
      if (isDimClickForbidden) {
        return;
      }
      if (typeof isHideAreaFixed !== 'undefined' && isHideAreaFixed) {
        return;
      }
      setIsClicked(true);
      const target = e.target as Element;
      const isClickedOut = target.closest('.modal-box__inner') || target.closest('#popover');

      if (isClickedOut !== null) {
        setIsClicked(false);
        return;
      }

      setTimeout(() => setIsClicked(false), 500);
      onCancel && onCancel();
    };

    return (
      <>
        <SC.DarkLayer css={SC.fullscreen} depth={depth} />
        <SC.Container
          {...rest}
          ref={ref}
          css={SC.fullscreen}
          onMouseDown={handleClickOutside}
          className={className}
          depth={depth}
          style={boxWrapperStyle}
        >
          <SC.InnerContainer css={{ width, height, borderRadius }} className='modal-box__inner' style={boxStyle}>
            {!hideTitle && (
              <SC.Header mode={theme} hasBorderBottom={hasBorderBottom} className='modal-header-wrapper'>
                <SC.Title className='modal-title'>{title}</SC.Title>

                {subChildren && onSubAction && <SC.SubBtn onClick={onSubAction}>{subChildren}</SC.SubBtn>}
              </SC.Header>
            )}
            <SC.Content className='modal-content-wrapper'>{children}</SC.Content>

            {!hideFooter && (
              <SC.Footer className='modal-footer-wrapper' mode={theme}>
                <SC.FooterFlexBox>
                  <SC.FooterLeft>{resetLabel && <ResetBtn label={resetLabel} onClick={onReset} />}</SC.FooterLeft>

                  <SC.ButtonGroup>
                    {!hideCancelBtn && <Button label={cancelText} themeType='secondary' onClick={onCancel} />}
                    {(confirmText || onConfirm) && (
                      <Button
                        label={confirmText}
                        themeType={isConfirmError ? 'error' : 'primary'}
                        onClick={() => {
                          setIsConfirmClicked(true);
                          onConfirm && onConfirm();
                        }}
                        disabled={disabled || isConfirmClicked}
                      />
                    )}
                  </SC.ButtonGroup>
                </SC.FooterFlexBox>
              </SC.Footer>
            )}
          </SC.InnerContainer>
        </SC.Container>
      </>
    );
  },
);

export default Modal;
